import React, { useMemo, useState } from "react";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import { TableSortDirection } from "@amzn/meridian/table/table";

interface HeaderCell {
    id: string;
    label: string;
    sortValue: string;
    order: number;
}

interface TableConfig {
    tableHeader: HeaderCell[];
    filter: null | any;
    label: string;
    emptyMessage: string;
    displayAdditionCells: boolean;
}

interface RowData {
    [key: string]: string | number | JSX.Element;
}

interface GeneralizedTableProps {
    config: TableConfig;
    rows: RowData[];
}

interface SortConfig {
    sortColumn: string | null;
    sortDirection: TableSortDirection;
}

export const PropertySpaceTableComponent: React.FC<GeneralizedTableProps> = ({ config, rows }) => {
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        sortColumn: null,
        sortDirection: "ascending",
    });

    const sortedRows = useMemo(() => {
        const { sortColumn, sortDirection } = sortConfig;
        if (!sortColumn) return rows;

        const header = config.tableHeader.find(h => h.id === sortColumn);
        if (!header || !header.sortValue) return rows;

        return [...rows].sort((a, b) => {
            const aValue = a[header.sortValue];
            const bValue = b[header.sortValue];

            if (aValue < bValue) return sortDirection === "ascending" ? -1 : 1;
            if (aValue > bValue) return sortDirection === "ascending" ? 1 : -1;

            return 0;
        });
    }, [rows, sortConfig, config.tableHeader]);

    const onSort = ({ sortColumn, sortDirection }: SortConfig) => {
        setSortConfig({ sortColumn, sortDirection });
    };

    const scrollableStyle: React.CSSProperties = {
        overflowX: "auto",
        overflowY: "hidden",
        maxWidth: "100%",
    };

    const renderHeaderCell = (header: HeaderCell) => (
        <TableCell
            key={header.id}
            alignmentHorizontal={header.id === "area" ? "end" : "start"}
            sortColumn={header.sortValue || null}
        >
            {header.label}
        </TableCell>
    );

    const renderRowCell = (header: HeaderCell, row: RowData) => (
        <TableCell key={header.id} alignmentHorizontal={header.id === "area" ? "end" : "start"}>
            {row[header.id]}
        </TableCell>
    );

    return (
        <>
            {config.label && <h2>{config.label}</h2>}
            <div style={scrollableStyle}>
                <Table
                    headerRows={1}
                    showDividers={true}
                    spacing="small"
                    showStripes={true}
                    sortColumn={sortConfig.sortColumn}
                    sortDirection={sortConfig.sortDirection}
                    onSort={onSort}
                >
                    <TableRow highlightOnHover={true}>
                        {config.tableHeader.sort((a, b) => a.order - b.order).map(renderHeaderCell)}
                    </TableRow>
                    {sortedRows.map((row, rowIndex) => (
                        <TableRow key={rowIndex} highlightOnHover={true}>
                            {config.tableHeader.map(header => renderRowCell(header, row))}
                        </TableRow>
                    ))}
                </Table>
            </div>
        </>
    );
};
